import { FeatureFlagName } from '@/modules/featureFlags/featureFlags.types';

/**
 * Feature announcement IDs with type safety.
 * Add new announcements here to maintain type safety.
 */
export const FeatureAnnouncementId = {
  IMPROVED_SUMMARY_PAGE_2026_10_28: 'improved_summary_page_2026_10_28',
  TRUST_SUMMARIES_2026_10_21: 'trust_summaries_2026_10_21',
  AI_ASSISTANT_SIMPLE_TASKS_2026_09_18: 'ai_assistant_simple_tasks_2026_09_18',
  VALIDATION_QUESTIONS_2026_09_04: 'validation_questions_2026_09_04',
  ESTATE360_VISUAL_REFRESH_2026_06_02: 'estate360_visual_refresh_2026_06_02',
  IMPROVED_AI_SUGGESTIONS_2026_07_08: 'improved_ai_suggestions_2026_07_08',
  PA_INHERITANCE_TAX_2026_07_30: 'pa_inheritance_tax_2026_07_30',
  NJ_INHERITANCE_TAX_2026_08_18: 'nj_inheritance_tax_2026_08_18',
  LUMINARY_AI_ASSISTANT_2026_07_30: 'luminary_ai_assistant_2026_07_30',
  // Add new announcement IDs here as they are created
} as const;

export type FeatureAnnouncementId =
  (typeof FeatureAnnouncementId)[keyof typeof FeatureAnnouncementId];

export interface FeatureAnnouncementConfig {
  /** Unique identifier for the announcement - must match the modal component ID */
  id: FeatureAnnouncementId;
  /** Optional feature flag that must be enabled for this announcement to show */
  featureFlagName?: FeatureFlagName;
  /** Date when this announcement should start showing */
  startDate: Date;
  /** Date when this announcement should stop showing */
  endDate: Date;
  /** Title shown in the FeatureAnnouncer card */
  announcementTitle: string;
  /** Short description shown in the FeatureAnnouncer card */
  announcementBlurb: string;
}

/**
 * Configuration for all feature announcements.
 * Add new announcements here following best practices:
 * - Set realistic start/end dates
 * - Include feature flag if the feature is gated
 * - Create a corresponding modal component with the same ID
 *
 * The modal component itself will contain the headline, body, image, and action URLs.
 */
export const FEATURE_ANNOUNCEMENTS: FeatureAnnouncementConfig[] = [
  {
    id: FeatureAnnouncementId.IMPROVED_SUMMARY_PAGE_2026_10_28,
    featureFlagName: 'waterfall_summary_page_update',
    startDate: new Date('2026-10-28T00:00:00-07:00'),
    endDate: new Date('2026-12-09T23:59:59-08:00'),
    announcementTitle: 'Improved summary page',
    announcementBlurb:
      'Each death event gets its own page, with larger text and more detail.',
  },
  {
    id: FeatureAnnouncementId.TRUST_SUMMARIES_2026_10_21,
    featureFlagName: 'document_summary_gpt61_sol',
    // Draft window. Confirm model rollout and release dates before publication;
    // Platform PR #19397 changes the trust prompt, not model routing.
    startDate: new Date('2026-10-21T00:00:00-04:00'),
    endDate: new Date('2026-12-02T23:59:59-05:00'),
    announcementTitle: 'Updated document summaries',
    announcementBlurb:
      'A more capable model family for richer, more detailed document summaries.',
  },
  {
    id: FeatureAnnouncementId.AI_ASSISTANT_SIMPLE_TASKS_2026_09_18,
    startDate: new Date('2026-09-18'),
    endDate: new Date('2026-10-30'),
    announcementTitle: 'AI Assistant Simple Tasks',
    announcementBlurb:
      'Create and update core client data with the AI Assistant',
  },
  {
    id: FeatureAnnouncementId.VALIDATION_QUESTIONS_2026_09_04,
    startDate: new Date('2026-09-04'),
    endDate: new Date('2026-10-16'),
    announcementTitle: 'Validation questions',
    announcementBlurb:
      'Provide additional context to increase data extraction accuracy',
  },
  {
    id: FeatureAnnouncementId.ESTATE360_VISUAL_REFRESH_2026_06_02,
    startDate: new Date('2026-06-02'),
    endDate: new Date('2026-07-14'),
    announcementTitle: 'Estate360 visual refresh',
    announcementBlurb:
      'The Estate360 has undergone a comprehensive visual upgrade.',
  },
  {
    id: FeatureAnnouncementId.LUMINARY_AI_ASSISTANT_2026_07_30,
    startDate: new Date('2026-07-30'),
    endDate: new Date('2026-09-10'),
    announcementTitle: 'Luminary AI Assistant',
    announcementBlurb:
      "Ask questions across all of a household's documents and data.",
  },
  {
    id: FeatureAnnouncementId.IMPROVED_AI_SUGGESTIONS_2026_07_08,
    startDate: new Date('2026-07-08'),
    endDate: new Date('2026-08-19'),
    announcementTitle: 'Improved AI Suggestions',
    announcementBlurb:
      'More accurate suggestions across extracted estate planning data.',
  },
  {
    id: FeatureAnnouncementId.PA_INHERITANCE_TAX_2026_07_30,
    startDate: new Date('2026-07-30'),
    endDate: new Date('2026-09-10'),
    announcementTitle: 'Pennsylvania inheritance tax',
    announcementBlurb:
      'Model estate waterfall values with Pennsylvania inheritance tax included.',
  },
  {
    id: FeatureAnnouncementId.NJ_INHERITANCE_TAX_2026_08_18,
    startDate: new Date('2026-08-18'),
    endDate: new Date('2026-09-29'),
    announcementTitle: 'New Jersey inheritance tax',
    announcementBlurb:
      'Model estate waterfall values with New Jersey inheritance tax included.',
  },
];
